import { FormField } from '@/components/forms';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import type { TriggerAction } from '../../types';

interface WebhookFormProps {
  action: TriggerAction;
  onChange: (patch: Partial<TriggerAction>) => void;
}

/**
 * Доступные плейсхолдеры payload/заголовков (плоские ключи payload триггера,
 * рендерятся через Handlebars — синтаксис {{key}}). Соответствуют полям
 * EnrichedUserContext в backend trigger-payload-enricher.service.ts.
 */
const PLACEHOLDER_HINT =
  'Плейсхолдеры (Handlebars): {{userFirstName}} {{userLastName}} {{userFullName}} ' +
  '{{userPhone}} {{userEmail}} {{userUsername}} {{telegramId}} {{userUtmSource}} ' +
  '{{userTags}} {{userLanguage}} {{currentDate}}';

/**
 * WebhookForm
 *
 * Форма действия отправки webhook. Поддерживает кастомные HTTP-заголовки
 * (например X-API-Key для внешнего приёмника лидов amoCRM) и шаблонизацию
 * payload/заголовков плейсхолдерами события.
 *
 * @returns {React.ReactElement} React компонент
 */
export function WebhookForm({ action, onChange }: WebhookFormProps) {
  const headers = action.headers ?? {};
  const headerEntries = Object.entries(headers);

  const updateHeaderKey = (oldKey: string, newKey: string) => {
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(headers)) {
      next[k === oldKey ? newKey : k] = v;
    }
    onChange({ headers: next });
  };

  const updateHeaderValue = (key: string, value: string) => {
    onChange({ headers: { ...headers, [key]: value } });
  };

  const removeHeader = (key: string) => {
    const next = { ...headers };
    delete next[key];
    onChange({ headers: next });
  };

  const addHeader = () => {
    // Пустой ключ — пользователь сразу его заполнит. Дубль '' невозможен
    // (объект), поэтому добавляем только если пустого ключа ещё нет.
    if (Object.prototype.hasOwnProperty.call(headers, '')) {
      return;
    }
    onChange({ headers: { ...headers, '': '' } });
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <FormField label="URL" name="url">
        <Input
          value={action.url ?? ''}
          onChange={(e) => onChange({ url: e.target.value || undefined })}
          placeholder="https://api.example.com/webhook"
        />
      </FormField>

      <FormField label="Метод" name="method">
        <Select
          value={action.method ?? 'POST'}
          onValueChange={(v) => onChange({ method: v as 'POST' | 'GET' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="GET">GET</SelectItem>
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="HTTP-заголовки"
        name="headers"
        className="md:col-span-2"
        hint="Кастомные заголовки запроса. Для amoCRM-приёмника лидов задайте X-API-Key с токеном. Значения поддерживают те же плейсхолдеры."
      >
        <div className="flex flex-col gap-2">
          {headerEntries.length === 0 && (
            <p className="text-sm text-muted-foreground">Заголовков нет</p>
          )}
          {headerEntries.map(([key, value], index) => (
            // Ключ-позиция: имена заголовков редактируемы и могут временно
            // дублироваться/пустеть, поэтому стабильнее опираться на индекс.
            // eslint-disable-next-line react/no-array-index-key
            <div key={`header-${index}`} className="flex items-center gap-2">
              <Input
                value={key}
                onChange={(e) => updateHeaderKey(key, e.target.value)}
                placeholder="X-API-Key"
                className="font-mono text-sm"
              />
              <Input
                value={value}
                onChange={(e) => updateHeaderValue(key, e.target.value)}
                placeholder="значение токена"
                className="font-mono text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeHeader(key)}
                aria-label="Удалить заголовок"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addHeader}
            className="self-start"
          >
            <Plus className="mr-1 h-4 w-4" />
            Добавить заголовок
          </Button>
        </div>
      </FormField>

      <FormField
        label="Payload (JSON)"
        name="payload"
        className="md:col-span-2"
        hint={PLACEHOLDER_HINT}
      >
        <Textarea
          value={action.payload ?? ''}
          onChange={(e) => onChange({ payload: e.target.value || undefined })}
          rows={6}
          placeholder={
            '{\n  "name": "{{userFullName}}",\n  "phone": "{{userPhone}}",\n  "source": "{{userUtmSource}}",\n  "message": "username=@{{userUsername}}; tags={{userTags}}"\n}'
          }
          className="font-mono text-sm"
        />
      </FormField>
    </div>
  );
}
